import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useEnvironment } from '@hooks/useEnvironment'
import { setAppError } from '@redux/slices/workflowSlice'
import { AppErrorType } from '@typesApp/AppError'
import { connectorActionModuleSelector } from '@redux/slices/connectorSlice'
import { useChangeInsuranceStatusMutation } from '@redux/queries/claimRecords'
import { InsuranceUsage } from '@typesApp/ClaimData'
import { Logger } from '@utils/logger'
import { isGatewayTimeout } from '@utils/errorUtil'
import { SubmitErrorResponse } from '@typesApp/SubmitResponse'
import { SYNC_INSURANCE } from '@constants/index'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { InsuranceUsageHandlerCallback, InsuranceUsageHandlerType, InsuranceUsageUtil } from '@utils/insuranceUsageUtil'
import { usePollClaimRecord } from './usePollClaimRecord'
import { useInvokeSyncInsuranceListeners } from './useInvokeSyncInsuranceListener'

type PromiseContext = {
  resolve: () => void
  reject: () => void
}

type PollingHandler = {
  shouldPoll: (claimRecord: ClaimRecord) => boolean
  poll: (claimRecord: ClaimRecord) => void
  handleResult: (claimRecord: ClaimRecord) => void
  handleError: (error?: unknown) => void
  promise: PromiseContext
}

type UseInsuranceUsageReturn = {
  isLoading: boolean
  enableInsuranceUsage: () => Promise<void>
  disableInsuranceUsage: () => Promise<void>
}

export const useInsuranceUsage = (): UseInsuranceUsageReturn => {
  const dispatch = useDispatch()
  const { cartPath } = useEnvironment()
  const [isLoading, setLoading] = useState(false)
  const { claimRecord, isLoading: isLoadingClaimRecord } = useClaimRecord()
  const pollClaimRecord = usePollClaimRecord()
  const invokeListeners = useInvokeSyncInsuranceListeners()
  const actionModule = useSelector(connectorActionModuleSelector)
  const [triggerChangeStatus] = useChangeInsuranceStatusMutation()

  const enableInsuranceUsage = (): Promise<void> => changeInsuranceUsage(InsuranceUsage.INSURANCE_ENABLED)

  const disableInsuranceUsage = (): Promise<void> => changeInsuranceUsage(InsuranceUsage.INSURANCE_DISABLED)

  const changeInsuranceUsage = (insuranceUsage: InsuranceUsage): Promise<void> => {
    if (!claimRecord) return Promise.resolve()

    return new Promise<void>((resolve, reject) => {
      setLoading(true)

      const handler = getPollingHandler(insuranceUsage, { resolve, reject })

      if (handler.shouldPoll(claimRecord)) {
        setTimeout(handler.poll, SYNC_INSURANCE.POLLING_INTERVAL_MS)
      } else {
        doUpdateWithPolling(insuranceUsage, handler)
      }
    })
  }

  const doUpdateWithPolling = (insuranceUsage: InsuranceUsage, handler: PollingHandler) => {
    if (!claimRecord) {
      handler.promise.resolve()
      return
    }

    triggerChangeStatus({ claimRecordId: claimRecord.id, useInsuranceStatus: insuranceUsage })
      .unwrap()
      .then(claimRecord => {
        handler.handleResult(claimRecord)
      })
      .catch(error => {
        handleSubmitError(handler, error)
      })
  }

  // handle timeout and poll until result is ready or time has exceeded
  const handleSubmitError = (handler: PollingHandler, result: SubmitErrorResponse) => {
    // poll on timeout
    if (isGatewayTimeout(result?.status)) {
      setTimeout(handler.poll, SYNC_INSURANCE.POLLING_INTERVAL_MS)
    } else {
      if (result?.data?.claimRecord) {
        handler.handleResult(result.data.claimRecord)
      } else {
        handler.handleError(result)
      }
    }
  }

  // handles successful and timeout result
  const getPollingHandler = (insuranceUsage: InsuranceUsage, promise: PromiseContext): PollingHandler => {
    const startTime = new Date()
    const util = InsuranceUsageUtil.getUtil(insuranceUsage)

    // notify commerce F/E on success
    const triggerCommerceAction = () => {
      if (insuranceUsage === InsuranceUsage.INSURANCE_ENABLED || insuranceUsage === InsuranceUsage.INSURANCE_DISABLED) {
        const action = insuranceUsage === InsuranceUsage.INSURANCE_ENABLED ? 'sync' : 'remove'
        invokeListeners(action)
      }

      // eslint-disable-next-line no-restricted-globals
      const isCart = window.location.pathname.includes(cartPath)
      if (isCart) {
        actionModule?.refreshCart()
      }

      promise.resolve()
    }

    const handlers = {
      success: () => triggerCommerceAction(),
      timeExceeded: () => handleError(),
    } as Record<InsuranceUsageHandlerType, InsuranceUsageHandlerCallback>

    const isResultReady = (claimRecord: ClaimRecord): boolean => {
      return util.isResultReady(claimRecord, startTime)
    }

    const handleResult = (claimRecord: ClaimRecord) => {
      setLoading(false)
      util.handleResult({ claimRecord, handlers, startTime })
    }

    const handleError = (error?: unknown) => {
      if (error) {
        Logger.error('Error updating insurance usage status', error)
      }
      setLoading(false)
      dispatch(setAppError(AppErrorType.GENERIC_ERROR))
      promise.resolve()
    }

    return {
      shouldPoll: util.shouldPoll,
      poll: () => pollClaimRecord(isResultReady, handleResult, handleError),
      handleResult,
      handleError,
      promise,
    }
  }

  return {
    isLoading: isLoading || Boolean(isLoadingClaimRecord),
    enableInsuranceUsage,
    disableInsuranceUsage,
  }
}
